<script lang="ts" setup>
import { required } from '@vuelidate/validators';
import CopyCreatedProjectLink from '@/app/project/create/CopyCreatedProjectLink.vue';
import { useProjectStore } from '@/store/projectStore';

interface Props {
  projectId: string;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const projectStore = useProjectStore();
const loading = ref(false);
const message = useAlerts();
const copiedProjectId = ref<string | null>(null);

const oldTitle = computed(
  () => projectStore.projectTitle(props.projectId) || '',
);

const formData = ref({
  title: `${oldTitle.value} Copy`,
});

const formRules = {
  title: [required],
};

async function saveProject() {
  loading.value = true;
  const { title: newTitle } = formData.value;

  try {
    const newProjectId = await projectStore.copyProject(
      props.projectId,
      newTitle,
    );

    message.success(
      $t('project.projectCopySuccess', {
        originalProjectTitle: oldTitle.value,
        copiedProjectTitle: newTitle,
      }),
    );

    // Show success stage with link component
    copiedProjectId.value = newProjectId;
  } catch (error) {
    message.error(error);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <UiModal
    ref="modalRef"
    :title="copiedProjectId ? '' : $t('project.copyProject')"
    :button-abort-text="$t('button.cancel')"
    @close="emit('close')"
  >
    <!-- Initial form stage -->
    <UiForm
      v-if="!copiedProjectId"
      class="copy-form"
      :rules="formRules"
      :form-data="formData"
      @form-submit="saveProject"
    >
      <template #default="{ formErrors }">
        <UiInput
          v-model="formData.title"
          :label="$t('project.projectTitle')"
          :placeholder="
            $t('project.copyOfProject', {
              project: oldTitle,
            })
          "
          :errors="formErrors.title"
        />
      </template>
      <template #bottom="{ formSubmit, hasErrors }">
        <UiButtonRow
          :cancel-button-text="$t('button.cancel')"
          :action-button-text="$t('button.copy')"
          :loading="loading"
          :disabled="hasErrors"
          expand
          @cancel="emit('close')"
          @action="formSubmit"
        />
      </template>
    </UiForm>

    <!-- Success stage -->
    <CopyCreatedProjectLink
      v-if="copiedProjectId"
      :link-id="copiedProjectId"
      @cancel="emit('close')"
    />
  </UiModal>
</template>

<style lang="scss" scoped>
.copy-form {
  :deep(.main) {
    margin-top: var(--space-8);
    margin-bottom: var(--space-16);
  }
}
</style>
